(function () {
  'use strict';

  class ProductsGridTabs {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.productsPerPage = parseInt(sectionEl.dataset.limitProducts, 10) || 12;
      this.emptyMessage = sectionEl.dataset.emptyMessage || 'There are no products in this collection yet';
      this.panel = sectionEl.querySelector('[data-tab-panel]');
      this.loadingOverlay = sectionEl.querySelector('[data-loading-overlay]');
      this.buttons = sectionEl.querySelectorAll('.products-grid-tab-btn');
      this.cache = new Map();

      this.buttons.forEach(btn => {
        btn.addEventListener('click', e => this._onTabClick(e));
        btn.addEventListener('keydown', e => this._onKeyDown(e));
      });

      // Snapshot SSR HTML so clicking back to the first tab always restores the original content
      this._firstBtn = sectionEl.querySelector('.products-grid-tab-btn');
      this._firstTabHtml = this.panel ? this.panel.innerHTML : '';
    }

    _onTabClick(e) {
      const btn = e.currentTarget;
      if (btn.classList.contains('is-active')) return;
      this._activateTab(btn);
    }

    _onKeyDown(e) {
      const tabs = Array.from(this.buttons);
      const i = tabs.indexOf(e.currentTarget);
      let next;
      if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === 'Home') next = tabs[0];
      else if (e.key === 'End') next = tabs[tabs.length - 1];
      else return;
      e.preventDefault();
      next.focus();
      this._activateTab(next);
    }

    _activateTab(btn) {
      this.buttons.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      if (this.panel && btn.id) this.panel.setAttribute('aria-labelledby', btn.id);

      // First tab: always restore the original SSR snapshot
      if (btn === this._firstBtn && this._firstTabHtml) {
        this.panel.innerHTML = this._firstTabHtml;
        if (typeof checkSwatches === 'function') checkSwatches();
        if (typeof initQuickView === 'function') initQuickView();
        return;
      }

      const tabId = btn.dataset.tabId;
      const excludedIds = btn.dataset.excludedIds || ',';

      // Always use pre-rendered template when available (ensures opt_in applies to all tab types)
      const tpl = this.section.querySelector(`[data-tab-template="${tabId}"]`);
      if (tpl) {
        this._loadFromTemplate(tabId);
      } else {
        const handle = btn.dataset.collectionHandle;
        this._loadFromAjax(handle, excludedIds);
      }
    }

    _loadFromTemplate(tabId) {
      const tpl = this.section.querySelector(`[data-tab-template="${tabId}"]`);
      if (!tpl) return;
      this.panel.innerHTML = tpl.innerHTML;
      if (typeof checkSwatches === 'function') checkSwatches();
      if (typeof initQuickView === 'function') initQuickView();
    }

    _showEmpty() {
      this.panel.innerHTML = `<li class="products-grid-tabs__empty">${this.emptyMessage}</li>`;
    }

    _loadFromAjax(handle, excludedIds) {
      if (!handle) {
        this._showEmpty();
        return;
      }

      if (this.cache.has(handle)) {
        this._renderItems(this.cache.get(handle), excludedIds);
        return;
      }

      this._setLoading(true);

      fetch(`/collections/${handle}?sections=products-tab-render`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then(data => {
          const html = data['products-tab-render'];
          if (!html) throw new Error('products-tab-render section missing from response');

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const items = Array.from(doc.querySelectorAll('.products-tab-render__list > li'));

          this.cache.set(handle, items);
          this._renderItems(items, excludedIds);
        })
        .catch(err => {
          console.error('[products-grid-tabs] AJAX error:', err);
        })
        .finally(() => {
          this._setLoading(false);
        });
    }

    _renderItems(items, excludedIds) {
      const excluded = excludedIds || ',';
      const fragment = document.createDocumentFragment();
      let count = 0;

      for (const item of items) {
        if (count >= this.productsPerPage) break;
        const pid = item.dataset.productId;
        if (pid && excluded.indexOf(`,${pid},`) !== -1) continue;
        fragment.appendChild(item.cloneNode(true));
        count++;
      }

      if (count === 0) {
        this._showEmpty();
        return;
      }

      this.panel.innerHTML = '';
      this.panel.appendChild(fragment);
      if (typeof checkSwatches === 'function') checkSwatches();
      if (typeof initQuickView === 'function') initQuickView();
    }

    _setLoading(active) {
      if (!this.loadingOverlay) return;
      if (active) {
        this.loadingOverlay.classList.add('is-visible');
        this.loadingOverlay.setAttribute('aria-hidden', 'false');
      } else {
        this.loadingOverlay.classList.remove('is-visible');
        this.loadingOverlay.setAttribute('aria-hidden', 'true');
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-pg-tabs]').forEach(el => {
      if (!el._pgTabsInstance) {
        el._pgTabsInstance = new ProductsGridTabs(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('[data-pg-tabs]');
    if (el) el._pgTabsInstance = new ProductsGridTabs(el);
  });

  document.addEventListener('shopify:section:deselect', e => {
    const el = e.target.querySelector('[data-pg-tabs]');
    if (el) el._pgTabsInstance = null;
  });

  // Theme editor: clicking a tab block highlights it — show that tab
  document.addEventListener('shopify:block:select', e => {
    const btn = e.target.closest('.products-grid-tab-btn');
    if (!btn) return;
    const sectionEl = btn.closest('[data-pg-tabs]');
    if (sectionEl && sectionEl._pgTabsInstance) sectionEl._pgTabsInstance._activateTab(btn);
  });

  // Theme editor: deselecting a block resets to first tab
  document.addEventListener('shopify:block:deselect', e => {
    const btn = e.target.closest('.products-grid-tab-btn');
    if (!btn) return;
    const sectionEl = btn.closest('[data-pg-tabs]');
    if (!sectionEl || !sectionEl._pgTabsInstance) return;
    const firstBtn = sectionEl.querySelector('.products-grid-tab-btn');
    if (firstBtn) sectionEl._pgTabsInstance._activateTab(firstBtn);
  });
})();
